// src/pages/Settings.js

import { useEffect, useState } from "react";
import api from "../constant/api";
import { toast } from "react-toastify";

export default function Settings() {
  const [companies, setCompanies] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    getSettings();
  }, []);

  const getSettings = async () => {
    try {
      const res = await api.get("/settings/getSettings");
      // Backend response handles multi-profile list
      if (res.data && Array.isArray(res.data.data)) {
        setCompanies(res.data.data);
      } else if (res.data.data) {
        // Fallback backward compatibility for single company data
        setCompanies([res.data.data]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch profiles");
    }
  };

  const handleFieldChange = (companyIndex, fieldName, value) => {
    const updatedCompanies = [...companies];
    updatedCompanies[companyIndex] = {
      ...updatedCompanies[companyIndex],
      [fieldName]: value,
    };
    setCompanies(updatedCompanies);
  };

  const addNewCompanyProfile = () => {
    const freshProfile = {
      company_name: `New Company ${companies.length + 1}`,
      address: "",
      email: "",
      phone: "",
      office_number: "",
      gst_no: "",
      pan_no: "",
      invoice_prefix: "INV-",
      invoice_start: 1,
      quotation_prefix: "QTN-",
      quotation_start: 1,
      order_prefix: "ORD-",
      order_start: 1,
      po_prefix: "PO-",
      po_start: 1,
    };
    setCompanies([...companies, freshProfile]);
    setActiveTab(companies.length); // Direct shift to newly built profile tab
  };

  const removeCompanyProfile = (indexToRemove) => {
    if (window.confirm("Are you sure you want to delete this company profile?")) {
      const filtered = companies.filter((_, idx) => idx !== indexToRemove);
      setCompanies(filtered);
      setActiveTab(Math.max(0, indexToRemove - 1));
    }
  };

  const saveSettings = async () => {
    try {
      const res = await api.post("/settings/saveSettings", { companies });
      toast.success(res.data?.message || "All configurations saved successfully!");
      getSettings();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save adjustments");
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
      
      {/* TOP HEADER SECTION */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Profiles</h1>
          <p className="text-sm text-gray-500">Configure corporate records, fiscal parameters, and tracking sequences.</p>
        </div>
        <button
          onClick={addNewCompanyProfile}
          className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white font-medium px-5 py-2.5 rounded-xl shadow-sm transition flex items-center justify-center gap-2 text-sm"
        >
          <span>➕</span> Add New Company
        </button>
      </div>

      {companies.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-gray-200 rounded-2xl p-12 text-center">
          <p className="text-gray-500 font-medium mb-4">No registered profiles found.</p>
          <button onClick={addNewCompanyProfile} className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 rounded-xl shadow">
            Create First Profile
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          
          {/* PROFILE SELECTION LIST (LEFT COLUMN SIDEBAR) */}
          <div className="lg:col-span-4 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 space-y-2">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider px-2 mb-2">Registered Companies List</p>
            <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
              {companies.map((comp, idx) => (
                <div
                  key={idx}
                  onClick={() => setActiveTab(idx)}
                  className={`w-full text-left px-4 py-3.5 rounded-xl transition font-medium text-sm flex items-center justify-between cursor-pointer group border ${
                    activeTab === idx
                      ? "bg-indigo-50 border-indigo-200 text-indigo-700 shadow-sm"
                      : "bg-white border-gray-100 text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`}
                >
                  <div className="flex flex-col min-w-0 truncate">
                    <span className="font-semibold truncate">{comp.company_name || `Untitled Profile ${idx + 1}`}</span>
                    <span className="text-xs text-gray-400 mt-0.5 truncate">{comp.gst_no || "No GST Registration"}</span>
                  </div>
                  {companies.length > 1 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeCompanyProfile(idx);
                      }}
                      className="text-gray-400 hover:text-red-600 p-1.5 rounded-lg hover:bg-white transition opacity-100 lg:opacity-0 lg:group-hover:opacity-100"
                      title="Delete Profile"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* ACTIVE MANAGEMENT CONTROLS PANEL (RIGHT COLUMN DETAIL BOX) */}
          <div className="lg:col-span-8 bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
            <div className="p-4 md:p-6 border-b border-gray-100 bg-gray-50/50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <h2 className="text-lg font-bold text-gray-800 truncate">
                Editing: <span className="text-indigo-600">{companies[activeTab]?.company_name}</span>
              </h2>
            </div>

            <div className="p-4 md:p-6 space-y-6">
              {/* PRIMARY KYC CORPORATE IDENTITY DATA */}
              <div>
                <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-4 border-b border-indigo-100 pb-2">1. Identity & Communications Profile</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Company Name</label>
                    <input
                      type="text"
                      value={companies[activeTab]?.company_name || ""}
                      onChange={(e) => handleFieldChange(activeTab, "company_name", e.target.value)}
                      className="border border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-xl w-full p-2.5 mt-1 text-sm bg-white outline-none transition"
                      placeholder="e.g., Acme Technologies Pvt Ltd"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Email Address</label>
                    <input
                      type="email"
                      value={companies[activeTab]?.email || ""}
                      onChange={(e) => handleFieldChange(activeTab, "email", e.target.value)}
                      className="border border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-xl w-full p-2.5 mt-1 text-sm bg-white outline-none transition"
                      placeholder="billing@company.com"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Mobile Phone</label>
                    <input
                      type="text"
                      value={companies[activeTab]?.phone || ""}
                      onChange={(e) => handleFieldChange(activeTab, "phone", e.target.value)}
                      className="border border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-xl w-full p-2.5 mt-1 text-sm bg-white outline-none transition"
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Office Landline Number</label>
                    <input
                      type="text"
                      value={companies[activeTab]?.office_number || ""}
                      onChange={(e) => handleFieldChange(activeTab, "office_number", e.target.value)}
                      className="border border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-xl w-full p-2.5 mt-1 text-sm bg-white outline-none transition"
                      placeholder="044-2345678"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-semibold text-gray-600">Registered Office Address</label>
                    <textarea
                      rows="2"
                      value={companies[activeTab]?.address || ""}
                      onChange={(e) => handleFieldChange(activeTab, "address", e.target.value)}
                      className="border border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-xl w-full p-2.5 mt-1 text-sm bg-white outline-none transition resize-none"
                      placeholder="Street Complex, City, State, Pincode"
                    />
                  </div>
                </div>
              </div>

              {/* STATUTORY TAX MATTERS */}
              <div>
                <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-4 border-b border-indigo-100 pb-2">2. Statutory Fiscal Parameters</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-600">GST Number</label>
                    <input
                      type="text"
                      value={companies[activeTab]?.gst_no || ""}
                      onChange={(e) => handleFieldChange(activeTab, "gst_no", e.target.value)}
                      className="border border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-xl w-full p-2.5 mt-1 text-sm bg-white uppercase outline-none transition"
                      placeholder="33AAAAA0000A1Z1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">PAN Card Number</label>
                    <input
                      type="text"
                      value={companies[activeTab]?.pan_no || ""}
                      onChange={(e) => handleFieldChange(activeTab, "pan_no", e.target.value)}
                      className="border border-gray-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded-xl w-full p-2.5 mt-1 text-sm bg-white uppercase outline-none transition"
                      placeholder="ABCDE1234F"
                    />
                  </div>
                </div>
              </div>

              {/* SEQUENCING CONTROLS */}
              <div>
                <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-4 border-b border-indigo-100 pb-2">3. Sequence Document Management</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                  
                  {/* INVOICE TRACK */}
                  <div className="bg-gray-50/70 p-3 rounded-xl border border-gray-100 space-y-2">
                    <p className="text-xs font-bold text-gray-700 border-b pb-1">📄 Sales Invoice</p>
                    <div>
                      <label className="text-[10px] text-gray-500 font-medium">Prefix</label>
                      <input type="text" value={companies[activeTab]?.invoice_prefix || ""} onChange={(e) => handleFieldChange(activeTab, "invoice_prefix", e.target.value)} className="border rounded-lg bg-white p-1.5 w-full text-xs font-bold" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 font-medium">Next No.</label>
                      <input type="number" value={companies[activeTab]?.invoice_start || ""} onChange={(e) => handleFieldChange(activeTab, "invoice_start", Number(e.target.value))} className="border rounded-lg bg-white p-1.5 w-full text-xs font-bold" />
                    </div>
                  </div>

                  {/* QUOTATION TRACK */}
                  <div className="bg-gray-50/80 p-3 rounded-xl border border-gray-100 space-y-2">
                    <p className="text-xs font-bold text-gray-700 border-b pb-1">💼 Quotation</p>
                    <div>
                      <label className="text-[10px] text-gray-500 font-medium">Prefix</label>
                      <input type="text" value={companies[activeTab]?.quotation_prefix || ""} onChange={(e) => handleFieldChange(activeTab, "quotation_prefix", e.target.value)} className="border rounded-lg bg-white p-1.5 w-full text-xs font-bold" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 font-medium">Next No.</label>
                      <input type="number" value={companies[activeTab]?.quotation_start || ""} onChange={(e) => handleFieldChange(activeTab, "quotation_start", Number(e.target.value))} className="border rounded-lg bg-white p-1.5 w-full text-xs font-bold" />
                    </div>
                  </div>

                  {/* SALES ORDER TRACK */}
                  <div className="bg-gray-50/80 p-3 rounded-xl border border-gray-100 space-y-2">
                    <p className="text-xs font-bold text-gray-700 border-b pb-1">🛒 Order Prefix</p>
                    <div>
                      <label className="text-[10px] text-gray-500 font-medium">Prefix</label>
                      <input type="text" value={companies[activeTab]?.order_prefix || ""} onChange={(e) => handleFieldChange(activeTab, "order_prefix", e.target.value)} className="border rounded-lg bg-white p-1.5 w-full text-xs font-bold" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 font-medium">Next No.</label>
                      <input type="number" value={companies[activeTab]?.order_start || ""} onChange={(e) => handleFieldChange(activeTab, "order_start", Number(e.target.value))} className="border rounded-lg bg-white p-1.5 w-full text-xs font-bold" />
                    </div>
                  </div>

                  {/* PURCHASE ORDER TRACK */}
                  <div className="bg-gray-50/80 p-3 rounded-xl border border-gray-100 space-y-2">
                    <p className="text-xs font-bold text-gray-700 border-b pb-1">📦 PO Prefix</p>
                    <div>
                      <label className="text-[10px] text-gray-500 font-medium">Prefix</label>
                      <input type="text" value={companies[activeTab]?.po_prefix || ""} onChange={(e) => handleFieldChange(activeTab, "po_prefix", e.target.value)} className="border rounded-lg bg-white p-1.5 w-full text-xs font-bold" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 font-medium">Next No.</label>
                      <input type="number" value={companies[activeTab]?.po_start || ""} onChange={(e) => handleFieldChange(activeTab, "po_start", Number(e.target.value))} className="border rounded-lg bg-white p-1.5 w-full text-xs font-bold" />
                    </div>
                  </div>

                </div>
              </div>

              {/* SAVE PROFILE BUTTON BAR */}
              <div className="pt-4 border-t border-gray-100 flex justify-end">
                <button
                  onClick={saveSettings}
                  className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded-xl shadow-md transition text-sm"
                >
                  Save System Settings
                </button>
              </div>

            </div>
          </div>

        </div>
      )}
    </div>
  );
}